import React, { useEffect, useState } from 'react';
import {
  Upload,
  Image as ImageIcon,
  Video,
  Link as LinkIcon,
  Sparkles,
  Calendar,
  Trash2,
  Loader2,
  Check,
  X,
  Wand2,
  Edit3,
  Save,
  RefreshCw,
  Instagram,
  Clock,
  Hash,
  Eye,
  Database,
  Brain,
  CalendarClock,
  ChevronRight,
  BarChart3,
  ShieldCheck,
} from 'lucide-react';
import ApprovalStep from '@/components/zera/ApprovalStep';
import InstagramConnect from '@/components/zera/InstagramConnect';
import {
  BrandAsset,
  BrandVoiceProfile,
  ScheduledPost,
  GeneratedWeek,
  listAssets,
  uploadAssetFile,
  createAsset,
  deleteAsset,
  listVoices,
  analyzeAssets,
  deleteVoice,
  generateWeek,
  listScheduledPosts,
  listWeeks,
  updateScheduledPost,
  deleteScheduledPost,
  deleteWeek,
  generatePostImage,
} from '@/lib/brandStudio';

type Step = 1 | 2 | 3 | 4 | 5;

const BrandStudio: React.FC = () => {
  const [step, setStep] = useState<Step>(1);

  // Step 1
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  // URL add form
  const [urlForm, setUrlForm] = useState({
    asset_type: 'social_profile' as BrandAsset['asset_type'],
    url: '',
    title: '',
    source_platform: 'instagram',
    notes: '',
  });

  // Voices
  const [voices, setVoices] = useState<BrandVoiceProfile[]>([]);
  const [activeVoiceId, setActiveVoiceId] = useState<string | null>(null);

  // Step 2
  const [generatingWeek, setGeneratingWeek] = useState(false);
  const [generateImages, setGenerateImages] = useState(false);

  // Step 3
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [weeks, setWeeks] = useState<GeneratedWeek[]>([]);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<ScheduledPost>>({});

  // ---- Loaders ----
  const refreshAll = async () => {
    const [a, v, p, w] = await Promise.all([listAssets(), listVoices(), listScheduledPosts(), listWeeks()]);
    setAssets(a);
    setVoices(v);
    if (!activeVoiceId && v.length > 0) setActiveVoiceId(v[0].id);
    setPosts(p);
    setWeeks(w);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const activeVoice = voices.find((v) => v.id === activeVoiceId) || voices[0] || null;

  // ---- Step 1: Asset upload + analyze ----
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const result = await uploadAssetFile(file);
      if (result) {
        const type: BrandAsset['asset_type'] = file.type.startsWith('video') ? 'video' : 'image';
        await createAsset({
          asset_type: type,
          url: result.url,
          storage_path: result.path,
          title: file.name,
        });
      }
    }
    await refreshAll();
    setUploading(false);
  };

  const handleAddUrl = async () => {
    if (!urlForm.url.trim()) return;
    await createAsset({
      asset_type: urlForm.asset_type,
      url: urlForm.url.trim(),
      title: urlForm.title.trim() || urlForm.url.trim(),
      source_platform: urlForm.source_platform,
      notes: urlForm.notes.trim(),
    });
    setUrlForm({ ...urlForm, url: '', title: '', notes: '' });
    await refreshAll();
  };

  const toggleAssetSelection = (id: string) => {
    const next = new Set(selectedAssetIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedAssetIds(next);
  };

  const handleAnalyze = async () => {
    if (selectedAssetIds.size === 0) {
      setAnalyzeError('Select at least one asset to learn from.');
      return;
    }
    setAnalyzeError(null);
    setAnalyzing(true);
    try {
      const voice = await analyzeAssets(Array.from(selectedAssetIds), `Voice ${new Date().toLocaleDateString()}`);
      if (voice) {
        setActiveVoiceId(voice.id);
        await refreshAll();
        setStep(2);
      } else {
        setAnalyzeError('Analysis did not return a result. Please try again.');
      }
    } catch (e: any) {
      setAnalyzeError(e?.message || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  // ---- Step 2: Generate week ----
  const handleGenerateWeek = async () => {
    if (!activeVoice) {
      alert('Build a brand voice first (Step 1).');
      return;
    }
    setGeneratingWeek(true);
    const result = await generateWeek(activeVoice, undefined, generateImages);
    if (result) {
      await refreshAll();
      setStep(3);
    } else {
      alert('Week generation failed. Try again.');
    }
    setGeneratingWeek(false);
  };

  // ---- Step 3: Schedule / edit ----
  const startEdit = (p: ScheduledPost) => {
    setEditingPostId(p.id);
    setEditDraft({
      caption: p.caption,
      hashtags: p.hashtags,
      cta: p.cta,
      post_type: p.post_type,
      theme: p.theme,
      scheduled_for: p.scheduled_for,
      status: p.status,
    });
  };

  const saveEdit = async () => {
    if (!editingPostId) return;
    await updateScheduledPost(editingPostId, editDraft);
    setEditingPostId(null);
    setEditDraft({});
    await refreshAll();
  };

  const handleSchedulePost = async (p: ScheduledPost) => {
    await updateScheduledPost(p.id, { status: 'scheduled' });
    await refreshAll();
  };

  const handleApprovePost = async (p: ScheduledPost) => {
    await updateScheduledPost(p.id, { status: 'approved' });
    await refreshAll();
  };

  const handleDeletePost = async (id: string) => {
    if (!confirm('Delete this scheduled post?')) return;
    await deleteScheduledPost(id);
    await refreshAll();
  };

  // Generate a brand-aware image for a post that is missing one.
  const handleGeneratePostImage = async (p: ScheduledPost) => {
    await generatePostImage(p, activeVoice);
    await refreshAll();
  };


  const fmtDateTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // ====== UI ======

  return (
    <div className="bg-white">
      {/* Step Stepper Header */}
      <div className="bg-[#0D1B2A] px-6 py-5 border-b border-[#C9A961]/30">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
              <Brain className="w-5 h-5 text-[#C9A961]" />
            </div>
            <div>
              <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
                AI Brand Learning Studio
              </div>
              <h2 className="font-serif text-xl text-white font-light">Five-Step Social Posting Engine</h2>
            </div>
          </div>
          <button
            onClick={refreshAll}
            className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A] transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { n: 1, label: 'Learn from Assets', icon: Database, desc: 'Upload + analyze' },
            { n: 2, label: 'Generate Week', icon: Wand2, desc: 'AI-write 7 posts' },
            { n: 3, label: 'Schedule & Edit', icon: CalendarClock, desc: 'Review, edit, queue' },
            { n: 4, label: 'Admin Approval', icon: ShieldCheck, desc: 'Approve or send back' },
            { n: 5, label: 'Analytics & Connect', icon: BarChart3, desc: 'Instagram + insights' },
          ].map(({ n, label, icon: Icon, desc }) => {
            const active = step === n;
            const done = step > n;
            return (
              <button
                key={n}
                onClick={() => setStep(n as Step)}
                className={`group text-left p-4 border transition-all ${
                  active
                    ? 'bg-[#C9A961] border-[#C9A961] text-[#0D1B2A]'
                    : done
                    ? 'bg-[#C9A961]/10 border-[#C9A961]/40 text-white'
                    : 'bg-transparent border-white/15 text-white/70 hover:border-[#C9A961]/40'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-serif text-sm ${
                        active
                          ? 'bg-[#0D1B2A] text-[#C9A961]'
                          : done
                          ? 'bg-[#C9A961] text-[#0D1B2A]'
                          : 'bg-white/10 text-white/60'
                      }`}
                    >
                      {done ? <Check className="w-3.5 h-3.5" /> : n}
                    </div>
                    <Icon className="w-4 h-4" />
                  </div>
                  <ChevronRight className={`w-4 h-4 ${active ? 'text-[#0D1B2A]' : 'text-white/30'}`} />
                </div>
                <div className="font-sans text-[11px] tracking-[0.2em] uppercase font-medium">{label}</div>
                <div className={`font-sans text-[11px] mt-1 ${active ? 'text-[#0D1B2A]/70' : 'text-white/50'}`}>
                  {desc}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="p-6">
        {step === 1 && analyzeError && (
          <div className="mb-5 flex items-start gap-3 border border-red-300 bg-red-50 px-4 py-3">
            <X className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />
            <div>
              <div className="font-sans text-xs font-semibold text-red-700 uppercase tracking-wider">Analysis failed</div>
              <div className="font-sans text-xs text-red-600 mt-1">{analyzeError}</div>
            </div>
          </div>
        )}
        {step === 1 && (
          <Step1Assets
            assets={assets}
            selectedAssetIds={selectedAssetIds}
            toggleAssetSelection={toggleAssetSelection}
            handleFileUpload={handleFileUpload}
            uploading={uploading}
            urlForm={urlForm}
            setUrlForm={setUrlForm}
            handleAddUrl={handleAddUrl}
            handleAnalyze={handleAnalyze}
            analyzing={analyzing}
            onDelete={async (id) => {
              await deleteAsset(id);
              await refreshAll();
            }}
            voices={voices}
            activeVoiceId={activeVoiceId}
            setActiveVoiceId={setActiveVoiceId}
            onDeleteVoice={async (id) => {
              if (!confirm('Delete this brand voice?')) return;
              await deleteVoice(id);
              await refreshAll();
            }}
            goToStep={(s) => setStep(s)}
          />
        )}

        {step === 2 && (
          <Step2Voice
            voice={activeVoice}
            voices={voices}
            activeVoiceId={activeVoiceId}
            setActiveVoiceId={setActiveVoiceId}
            generatingWeek={generatingWeek}
            generateImages={generateImages}
            setGenerateImages={setGenerateImages}
            handleGenerateWeek={handleGenerateWeek}
            goToStep={(s) => setStep(s)}
          />
        )}

        {step === 3 && (
          <Step3Schedule
            posts={posts}
            weeks={weeks}
            editingPostId={editingPostId}
            editDraft={editDraft}
            setEditDraft={setEditDraft}
            startEdit={startEdit}
            saveEdit={saveEdit}
            cancelEdit={() => {
              setEditingPostId(null);
              setEditDraft({});
            }}
            handleApprovePost={handleApprovePost}
            handleSchedulePost={handleSchedulePost}
            handleDeletePost={handleDeletePost}
            onDeleteWeek={async (id) => {
              if (!confirm('Delete this generated week and all its posts?')) return;
              await deleteWeek(id);
              await refreshAll();
            }}
            fmtDateTime={fmtDateTime}
            goToStep={(s) => setStep(s)}
            voice={activeVoice}
            onGenerateImage={handleGeneratePostImage}
          />

        )}

        {step === 4 && (
          <ApprovalStep
            posts={posts}
            refresh={refreshAll}
            fmtDateTime={fmtDateTime}
            goToStep={(s) => setStep(s)}
          />
        )}

        {step === 5 && <InstagramConnect />}
      </div>
    </div>
  );
};

// ============ STEP 1 ============

interface Step1Props {
  assets: BrandAsset[];
  selectedAssetIds: Set<string>;
  toggleAssetSelection: (id: string) => void;
  handleFileUpload: (files: FileList | null) => void;
  uploading: boolean;
  urlForm: any;
  setUrlForm: (v: any) => void;
  handleAddUrl: () => void;
  handleAnalyze: () => void;
  analyzing: boolean;
  onDelete: (id: string) => Promise<void>;
  voices: BrandVoiceProfile[];
  activeVoiceId: string | null;
  setActiveVoiceId: (id: string) => void;
  onDeleteVoice: (id: string) => Promise<void>;
  goToStep: (s: Step) => void;
}

const Step1Assets: React.FC<Step1Props> = ({
  assets,
  selectedAssetIds,
  toggleAssetSelection,
  handleFileUpload,
  uploading,
  urlForm,
  setUrlForm,
  handleAddUrl,
  handleAnalyze,
  analyzing,
  onDelete,
  voices,
  activeVoiceId,
  setActiveVoiceId,
  onDeleteVoice,
  goToStep,
}) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 1 · Build the Learning Database</h3>
        <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
          Upload images and videos, or paste social profile/post URLs. The AI will analyze them to extract a
          unified brand voice — tone, vocabulary, themes, visual palette, signature phrases.
        </p>
      </div>

      {/* Upload area */}
      <div className="grid lg:grid-cols-2 gap-6">
        <label className="block border-2 border-dashed border-[#C9A961]/40 bg-[#FAF8F3] p-8 text-center cursor-pointer hover:border-[#C9A961] hover:bg-[#F3EFE6] transition-colors">
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files)}
            disabled={uploading}
          />
          <div className="w-14 h-14 mx-auto rounded-full bg-[#C9A961]/15 flex items-center justify-center mb-3">
            {uploading ? (
              <Loader2 className="w-6 h-6 text-[#C9A961] animate-spin" />
            ) : (
              <Upload className="w-6 h-6 text-[#C9A961]" />
            )}
          </div>
          <div className="font-serif text-lg text-[#0D1B2A]">
            {uploading ? 'Uploading…' : 'Drop images & videos'}
          </div>
          <div className="font-sans text-xs text-slate-500 mt-1">JPG, PNG, MP4, MOV — multiple OK</div>
        </label>

        {/* URL form */}
        <div className="border border-slate-200 p-6 bg-white">
          <div className="font-sans text-[11px] tracking-[0.2em] uppercase text-[#C9A961] mb-3">
            Or paste a social URL
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <select
                value={urlForm.asset_type}
                onChange={(e) => setUrlForm({ ...urlForm, asset_type: e.target.value })}
                className="border border-slate-200 px-3 py-2 font-sans text-sm bg-white"
              >
                <option value="social_profile">Profile</option>
                <option value="social_post">Post</option>
                <option value="reference_url">Reference URL</option>
              </select>
              <select
                value={urlForm.source_platform}
                onChange={(e) => setUrlForm({ ...urlForm, source_platform: e.target.value })}
                className="border border-slate-200 px-3 py-2 font-sans text-sm bg-white"
              >
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="youtube">YouTube</option>
                <option value="pinterest">Pinterest</option>
                <option value="website">Website</option>
                <option value="other">Other</option>
              </select>
            </div>
            <input
              type="text"
              placeholder="https://instagram.com/zeratravel"
              value={urlForm.url}
              onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
              className="w-full border border-slate-200 px-3 py-2 font-sans text-sm"
            />
            <input
              type="text"
              placeholder="Title (optional)"
              value={urlForm.title}
              onChange={(e) => setUrlForm({ ...urlForm, title: e.target.value })}
              className="w-full border border-slate-200 px-3 py-2 font-sans text-sm"
            />
            <textarea
              placeholder="Notes about what to learn from this (optional)"
              value={urlForm.notes}
              onChange={(e) => setUrlForm({ ...urlForm, notes: e.target.value })}
              className="w-full border border-slate-200 px-3 py-2 font-sans text-sm h-20 resize-none"
            />
            <button
              onClick={handleAddUrl}
              className="w-full font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center justify-center gap-2"
            >
              <LinkIcon className="w-3.5 h-3.5" /> Add to Database
            </button>
          </div>
        </div>
      </div>

      {/* Asset library */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h4 className="font-serif text-lg text-[#0D1B2A]">
              Asset Library{' '}
              <span className="font-sans text-sm text-slate-400 ml-2">({assets.length})</span>
            </h4>
            <p className="font-sans text-xs text-slate-500 mt-0.5">
              Select assets and click "Analyze & Learn" to build a brand voice.
            </p>
          </div>
          <button
            onClick={handleAnalyze}
            disabled={analyzing || selectedAssetIds.size === 0}
            className={`font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors flex items-center gap-2 ${
              selectedAssetIds.size > 0 && !analyzing
                ? 'bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {analyzing ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing…
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" /> Analyze {selectedAssetIds.size} & Learn
              </>
            )}
          </button>
        </div>

        {assets.length === 0 ? (
          <div className="border border-dashed border-slate-200 py-16 text-center bg-slate-50">
            <Database className="w-8 h-8 mx-auto text-slate-300 mb-3" />
            <p className="font-sans text-sm text-slate-400">No assets yet. Upload or paste a URL above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {assets.map((a) => {
              const selected = selectedAssetIds.has(a.id);
              const isMedia = a.asset_type === 'image' || a.asset_type === 'video';
              return (
                <div
                  key={a.id}
                  onClick={() => toggleAssetSelection(a.id)}
                  className={`group relative border-2 cursor-pointer overflow-hidden transition-all ${
                    selected
                      ? 'border-[#C9A961] ring-2 ring-[#C9A961]/30'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="aspect-square bg-slate-100 relative">
                    {a.asset_type === 'image' ? (
                      <img src={a.url} alt={a.title || ''} className="w-full h-full object-cover" />
                    ) : a.asset_type === 'video' ? (
                      <video src={a.url} className="w-full h-full object-cover" muted />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                        <LinkIcon className="w-8 h-8 text-[#C9A961] mb-2" />
                        <div className="font-sans text-[10px] tracking-wider uppercase text-slate-500">
                          {a.source_platform || 'URL'}
                        </div>
                        <div className="font-sans text-[11px] text-slate-700 mt-1 line-clamp-2 break-all">
                          {a.title || a.url}
                        </div>
                      </div>
                    )}
                    {selected && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[#C9A961] flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(a.id);
                      }}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white font-sans text-[9px] tracking-wider uppercase rounded">
                      {a.asset_type === 'image' && <ImageIcon className="w-2.5 h-2.5 inline mr-1" />}
                      {a.asset_type === 'video' && <Video className="w-2.5 h-2.5 inline mr-1" />}
                      {a.asset_type.replace('_', ' ')}
                    </div>
                  </div>
                  {isMedia && a.title && (
                    <div className="p-2 font-sans text-[11px] text-slate-600 truncate bg-white">{a.title}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Existing voices */}
      {voices.length > 0 && (
        <div className="border-t border-slate-200 pt-8">
          <h4 className="font-serif text-lg text-[#0D1B2A] mb-3">Trained Brand Voices</h4>
          <div className="grid md:grid-cols-2 gap-3">
            {voices.map((v) => {
              const active = v.id === activeVoiceId;
              return (
                <div
                  key={v.id}
                  className={`border p-4 ${
                    active ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <div className="font-serif text-base text-[#0D1B2A]">{v.name}</div>
                      <div className="font-sans text-[10px] tracking-wider uppercase text-slate-400 mt-0.5">
                        {new Date(v.created_at).toLocaleDateString()} · {(v.source_asset_ids || []).length} assets
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setActiveVoiceId(v.id)}
                        className={`font-sans text-[10px] tracking-wider uppercase px-2 py-1 border ${
                          active
                            ? 'bg-[#C9A961] text-white border-[#C9A961]'
                            : 'border-slate-200 text-slate-500 hover:border-[#C9A961]'
                        }`}
                      >
                        {active ? 'Active' : 'Use'}
                      </button>
                      <button
                        onClick={() => onDeleteVoice(v.id)}
                        className="w-7 h-7 flex items-center justify-center border border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {v.summary && (
                    <p className="font-sans text-xs text-slate-600 line-clamp-2">{v.summary}</p>
                  )}
                </div>
              );
            })}
          </div>
          {voices.length > 0 && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => goToStep(2)}
                className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center gap-2"
              >
                Continue to Step 2 <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ STEP 2 ============

interface Step2Props {
  voice: BrandVoiceProfile | null;
  voices: BrandVoiceProfile[];
  activeVoiceId: string | null;
  setActiveVoiceId: (id: string) => void;
  generatingWeek: boolean;
  generateImages: boolean;
  setGenerateImages: (b: boolean) => void;
  handleGenerateWeek: () => void;
  goToStep: (s: Step) => void;
}

const Step2Voice: React.FC<Step2Props> = ({
  voice,
  voices,
  activeVoiceId,
  setActiveVoiceId,
  generatingWeek,
  generateImages,
  setGenerateImages,
  handleGenerateWeek,
  goToStep,
}) => {
  if (!voice) {
    return (
      <div className="text-center py-16">
        <Brain className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <h3 className="font-serif text-xl text-[#0D1B2A]">No Brand Voice Yet</h3>
        <p className="font-sans text-sm text-slate-500 mt-2">
          Build a brand voice in Step 1 by analyzing reference assets.
        </p>
        <button
          onClick={() => goToStep(1)}
          className="mt-5 font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]"
        >
          Go to Step 1
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 2 · Generate a Week from the Voice</h3>
          <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
            Using your trained brand voice, AI will write 7 days of on-brand Instagram posts — captions,
            hashtags, post types, and image prompts.
          </p>
        </div>
        {voices.length > 1 && (
          <select
            value={activeVoiceId || ''}
            onChange={(e) => setActiveVoiceId(e.target.value)}
            className="border border-slate-200 px-3 py-2 font-sans text-sm bg-white"
          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Voice profile card */}
      <div className="border border-[#C9A961]/30 bg-gradient-to-br from-[#FAF8F3] to-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#C9A961]" />
          <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
            Active Brand Voice
          </div>
        </div>
        <h4 className="font-serif text-2xl text-[#0D1B2A] mb-3">{voice.name}</h4>
        {voice.summary && (
          <p className="font-sans text-sm text-slate-700 leading-relaxed mb-5 italic border-l-2 border-[#C9A961] pl-4">
            {voice.summary}
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          <VoiceChipGroup label="Tone" items={voice.tone} accent="#1A3B5C" />
          <VoiceChipGroup label="Recurring Themes" items={voice.themes} accent="#8B6F47" />
          <VoiceChipGroup label="Signature Vocabulary" items={voice.vocabulary} accent="#C9A961" />
          <VoiceChipGroup label="Avoid" items={voice.avoid_words} accent="#b94747" />
          <VoiceChipGroup label="Visual Palette" items={voice.visual_palette} accent="#C9A961" />
          <VoiceChipGroup label="Hashtag Strategy" items={voice.hashtag_strategy} accent="#1A3B5C" />
        </div>

        {voice.visual_style && (
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-1">
              Visual Style
            </div>
            <p className="font-sans text-sm text-slate-700">{voice.visual_style}</p>
          </div>
        )}

        {voice.example_captions && voice.example_captions.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-3">
              Example On-Brand Captions
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {voice.example_captions.slice(0, 3).map((c, i) => (
                <div key={i} className="border border-slate-200 p-3 bg-white">
                  <p className="font-sans text-xs text-slate-700 leading-relaxed whitespace-pre-line line-clamp-6">
                    {c}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Generate controls */}
      <div className="border border-slate-200 p-6 bg-white">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h4 className="font-serif text-lg text-[#0D1B2A]">Generate Next Week</h4>
            <p className="font-sans text-sm text-slate-500 mt-1">
              Creates 7 posts (Mon–Sun) with mixed post types and varied posting times.
            </p>
            <label className="mt-3 inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={generateImages}
                onChange={(e) => setGenerateImages(e.target.checked)}
                className="w-4 h-4 accent-[#C9A961]"
              />
              <span className="font-sans text-sm text-slate-700">
                Also generate AI images for each post (slower, ~30s)
              </span>
            </label>
          </div>
          <button
            onClick={handleGenerateWeek}
            disabled={generatingWeek}
            className={`font-sans text-[10px] tracking-[0.2em] uppercase px-6 py-3 transition-colors flex items-center gap-2 ${
              generatingWeek
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]'
            }`}
          >
            {generatingWeek ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…
              </>
            ) : (
              <>
                <Wand2 className="w-3.5 h-3.5" /> Generate 7 Days
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const VoiceChipGroup: React.FC<{ label: string; items: string[]; accent: string }> = ({
  label,
  items,
  accent,
}) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span
            key={`${it}-${i}`}
            className="font-sans text-[11px] px-2.5 py-1 border rounded-full bg-white"
            style={{ borderColor: `${accent}55`, color: accent }}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
};

// ============ STEP 3 ============

interface Step3Props {
  posts: ScheduledPost[];
  weeks: GeneratedWeek[];
  editingPostId: string | null;
  editDraft: Partial<ScheduledPost>;
  setEditDraft: (d: Partial<ScheduledPost>) => void;
  startEdit: (p: ScheduledPost) => void;
  saveEdit: () => void;
  cancelEdit: () => void;
  handleApprovePost: (p: ScheduledPost) => void;
  handleSchedulePost: (p: ScheduledPost) => void;
  handleDeletePost: (id: string) => void;
  onDeleteWeek: (id: string) => Promise<void>;
  fmtDateTime: (iso: string | null) => string;
  goToStep: (s: Step) => void;
  voice: BrandVoiceProfile | null;
  onGenerateImage: (p: ScheduledPost) => Promise<void>;
}

const Step3Schedule: React.FC<Step3Props> = ({
  posts,
  weeks,
  editingPostId,
  editDraft,
  setEditDraft,
  startEdit,
  saveEdit,
  cancelEdit,
  handleApprovePost,
  handleSchedulePost,
  handleDeletePost,
  onDeleteWeek,
  fmtDateTime,
  goToStep,
  voice,
  onGenerateImage,
}) => {
  const [filterWeek, setFilterWeek] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [genImageId, setGenImageId] = useState<string | null>(null);
  const [genImageError, setGenImageError] = useState<Record<string, string>>({});

  const handleGenerate = async (p: ScheduledPost) => {
    setGenImageId(p.id);
    setGenImageError((prev) => {
      const next = { ...prev };
      delete next[p.id];
      return next;
    });
    try {
      await onGenerateImage(p);
    } catch (e: any) {
      setGenImageError((prev) => ({ ...prev, [p.id]: e?.message || 'Image generation failed.' }));
    } finally {
      setGenImageId(null);
    }
  };


  const filtered = posts.filter((p) => {
    if (filterWeek !== 'all' && p.week_id !== filterWeek) return false;
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    return true;
  });

  const statusColors: Record<string, string> = {
    draft: 'bg-slate-100 text-slate-600 border-slate-300',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
    scheduled: 'bg-[#C9A961]/15 text-[#8B6F47] border-[#C9A961]/40',
    published: 'bg-blue-50 text-blue-700 border-blue-300',
    failed: 'bg-red-50 text-red-700 border-red-300',
  };

  if (posts.length === 0) {
    return (
      <div className="text-center py-16">
        <CalendarClock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <h3 className="font-serif text-xl text-[#0D1B2A]">No Scheduled Posts Yet</h3>
        <p className="font-sans text-sm text-slate-500 mt-2">Generate a week in Step 2 to populate the calendar.</p>
        <button
          onClick={() => goToStep(2)}
          className="mt-5 font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]"
        >
          Go to Step 2
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">
            Step 3 · Review, Edit & Schedule
          </h3>
          <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
            Review every AI-generated post, edit copy, change scheduled times, or remove. When the week looks
            good, send it to Step 4 for administrator approval before publishing.
          </p>
        </div>
        <button
          onClick={() => goToStep(4)}
          className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center gap-2"
        >
          Send to Approval <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 border-t border-b border-slate-200 py-4">
        <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400">Filter:</div>
        <select
          value={filterWeek}
          onChange={(e) => setFilterWeek(e.target.value)}
          className="border border-slate-200 px-3 py-1.5 font-sans text-xs bg-white"
        >
          <option value="all">All Weeks</option>
          {weeks.map((w) => (
            <option key={w.id} value={w.id}>
              {w.week_label || w.id.slice(0, 8)}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-slate-200 px-3 py-1.5 font-sans text-xs bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="scheduled">Scheduled</option>
          <option value="published">Published</option>
        </select>
        <div className="ml-auto font-sans text-xs text-slate-500">
          {filtered.length} of {posts.length} posts
        </div>
      </div>

      {/* Weeks summary */}
      {weeks.length > 0 && (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {weeks.slice(0, 6).map((w) => {
            const weekPosts = posts.filter((p) => p.week_id === w.id);
            return (
              <div key={w.id} className="border border-slate-200 p-4 bg-white">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-serif text-sm text-[#0D1B2A]">{w.week_label || 'Generated Week'}</div>
                    <div className="font-sans text-[10px] tracking-wider uppercase text-slate-400 mt-0.5">
                      {new Date(w.created_at).toLocaleDateString()} · {weekPosts.length} posts
                    </div>
                  </div>
                  <button
                    onClick={() => onDeleteWeek(w.id)}
                    className="w-7 h-7 flex items-center justify-center border border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['draft', 'approved', 'scheduled', 'published'].map((s) => {
                    const count = weekPosts.filter((p) => p.status === s).length;
                    if (count === 0) return null;
                    return (
                      <span
                        key={s}
                        className={`font-sans text-[9px] tracking-wider uppercase px-1.5 py-0.5 border rounded ${
                          statusColors[s] || ''
                        }`}
                      >
                        {count} {s}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Posts list */}
      <div className="space-y-4">
        {filtered.map((p) => {
          const isEditing = editingPostId === p.id;
          return (
            <div key={p.id} className="border border-slate-200 bg-white overflow-hidden">
              <div className="grid md:grid-cols-[180px_1fr] gap-0">
                {/* Image preview */}
                <div className="aspect-square md:aspect-auto bg-slate-100 relative group/img">
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center">
                      <ImageIcon className="w-6 h-6 text-slate-300 mb-2" />
                      <p className="font-sans text-[10px] text-slate-400 line-clamp-3">
                        {p.image_prompt || 'No image yet'}
                      </p>
                    </div>
                  )}

                  {/* AI image generation overlay — appears over a missing image */}
                  {!p.image_url && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0D1B2A]/0 group-hover/img:bg-[#0D1B2A]/55 transition-colors p-3">
                      {genImageId === p.id ? (
                        <div className="flex flex-col items-center gap-2 bg-[#0D1B2A]/70 px-3 py-3 rounded">
                          <Loader2 className="w-5 h-5 text-[#C9A961] animate-spin" />
                          <span className="font-sans text-[9px] tracking-[0.15em] uppercase text-white text-center">
                            Painting on-brand…
                          </span>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleGenerate(p)}
                          title="Use the caption + your brand voice to generate a matching image"
                          className="opacity-100 md:opacity-0 md:group-hover/img:opacity-100 transition-opacity font-sans text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651] flex flex-col items-center gap-1.5 rounded shadow-lg"
                        >
                          <Wand2 className="w-4 h-4" />
                          Generate with AI
                        </button>
                      )}
                      {genImageError[p.id] && genImageId !== p.id && (
                        <div className="mt-2 font-sans text-[9px] text-red-100 bg-red-600/80 px-2 py-1 rounded text-center line-clamp-3">
                          {genImageError[p.id]}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white font-sans text-[9px] tracking-wider uppercase rounded">
                    {p.post_type}
                  </div>
                </div>


                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Instagram className="w-3.5 h-3.5 text-[#C9A961]" />
                        <span className="font-serif text-base text-[#0D1B2A]">
                          {p.day_label || 'Untitled'}
                        </span>
                        {p.theme && (
                          <span className="font-sans text-[10px] tracking-wider uppercase text-slate-400">
                            · {p.theme}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {fmtDateTime(p.scheduled_for)}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`font-sans text-[10px] tracking-wider uppercase px-2 py-0.5 border rounded-full ${
                        statusColors[p.status] || statusColors.draft
                      }`}
                    >
                      {p.status}
                    </span>
                  </div>

                  {isEditing ? (
                    <div className="space-y-3">
                      <textarea
                        value={editDraft.caption || ''}
                        onChange={(e) => setEditDraft({ ...editDraft, caption: e.target.value })}
                        className="w-full border border-slate-200 px-3 py-2 font-sans text-sm h-32 resize-none"
                        placeholder="Caption"
                      />
                      <input
                        type="text"
                        value={(editDraft.hashtags || []).join(' ')}
                        onChange={(e) =>
                          setEditDraft({
                            ...editDraft,
                            hashtags: e.target.value.split(/\s+/).filter(Boolean),
                          })
                        }
                        className="w-full border border-slate-200 px-3 py-2 font-sans text-sm"
                        placeholder="#hashtags"
                      />
                      <input
                        type="text"
                        value={editDraft.cta || ''}
                        onChange={(e) => setEditDraft({ ...editDraft, cta: e.target.value })}
                        className="w-full border border-slate-200 px-3 py-2 font-sans text-sm"
                        placeholder="Call to action"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="datetime-local"
                          value={
                            editDraft.scheduled_for
                              ? new Date(editDraft.scheduled_for).toISOString().slice(0, 16)
                              : ''
                          }
                          onChange={(e) =>
                            setEditDraft({
                              ...editDraft,
                              scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : null,
                            })
                          }
                          className="border border-slate-200 px-3 py-2 font-sans text-sm"
                        />
                        <select
                          value={editDraft.post_type || 'Feed Post'}
                          onChange={(e) => setEditDraft({ ...editDraft, post_type: e.target.value })}
                          className="border border-slate-200 px-3 py-2 font-sans text-sm bg-white"
                        >
                          <option>Feed Post</option>
                          <option>Reel</option>
                          <option>Carousel</option>
                          <option>Story</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={saveEdit}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651] flex items-center gap-2"
                        >
                          <Save className="w-3.5 h-3.5" /> Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-slate-200 text-slate-600 hover:border-slate-400 flex items-center gap-2"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="font-sans text-sm text-slate-700 leading-relaxed whitespace-pre-line line-clamp-4">
                        {p.caption || <span className="italic text-slate-400">No caption</span>}
                      </p>

                      {p.cta && (
                        <p className="mt-2 font-sans text-xs text-[#8B6F47] italic border-l-2 border-[#C9A961] pl-3">
                          CTA: {p.cta}
                        </p>
                      )}

                      {p.hashtags && p.hashtags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {p.hashtags.slice(0, 8).map((h) => (
                            <span
                              key={h}
                              className="font-sans text-[10px] text-[#1A3B5C] bg-[#F3EFE6] border border-[#C9A961]/30 px-2 py-0.5 rounded-full"
                            >
                              {h}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => startEdit(p)}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-slate-200 text-slate-600 hover:border-[#C9A961] hover:text-[#C9A961] flex items-center gap-1.5"
                        >
                          <Edit3 className="w-3 h-3" /> Edit
                        </button>
                        {p.status === 'draft' && (
                          <button
                            onClick={() => handleApprovePost(p)}
                            className="font-sans text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5"
                          >
                            <Check className="w-3 h-3" /> Approve
                          </button>
                        )}
                        {(p.status === 'draft' || p.status === 'approved') && (
                          <button
                            onClick={() => handleSchedulePost(p)}
                            className="font-sans text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651] flex items-center gap-1.5"
                          >
                            <Calendar className="w-3 h-3" /> Schedule
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePost(p.id)}
                          className="ml-auto font-sans text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500 flex items-center gap-1.5"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BrandStudio;
